/**
 * `<SigningKeyPemDisplay>` — read-only PEM display in the Final
 * icon-titled card. Per NFR-SEC-17 the `SigningKey` type carries only
 * public fields; this component renders `publicKeyPem` and nothing else.
 */

import { Button, toast } from "@cometchat-ai/ui-dashboard";
import { Copy, ShieldCheck } from "../icons";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { SigningKey } from "../../lib/api/types";

export interface SigningKeyPemDisplayProps {
  signingKey: SigningKey;
  /** Optional "Rotated …" meta line under the card title. */
  rotatedLabel?: string;
  /** Optional secondary action (e.g. Rotate) rendered in the card header. */
  action?: ReactNode;
}

export function SigningKeyPemDisplay({
  signingKey,
  rotatedLabel,
  action,
}: SigningKeyPemDisplayProps) {
  const { t } = useTranslation("signing-keys");

  async function copy() {
    try {
      await navigator.clipboard.writeText(signingKey.publicKeyPem);
      toast.success(t("current.copyPemSuccess"));
    } catch {
      // Clipboard unavailable in some test environments — silently no-op.
    }
  }

  return (
    <section
      className="rounded-xl border border-border bg-card"
      data-testid={`signing-key-pem-${signingKey.kid}`}
    >
      <div className="flex items-start justify-between gap-3 px-5 py-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 space-y-0.5">
            <h3 className="text-sm font-semibold tracking-[0.01em] text-foreground">
              {t("current.heading")}
            </h3>
            {rotatedLabel ? <p className="text-xs text-muted-foreground">{rotatedLabel}</p> : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {action}
          <Button
            variant="outline"
            size="sm"
            onClick={copy}
            aria-label={t("current.copyPem")}
            className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-muted"
            data-testid={`signing-key-copy-${signingKey.kid}`}
          >
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            {t("current.copyPem")}
          </Button>
        </div>
      </div>

      <div className="-mx-5 border-t border-dashed border-border" />

      <div className="space-y-2.5 px-5 py-4">
        <code className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="rounded-[5px] bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] px-1.5 py-0.5 font-mono text-foreground">
            kid: {signingKey.kid}
          </span>
          <span>{signingKey.algorithm}</span>
        </code>
        <pre className="overflow-auto rounded-lg bg-muted p-3 text-xs leading-relaxed text-foreground">
          {signingKey.publicKeyPem}
        </pre>
      </div>
    </section>
  );
}
